import { useMutation, useQueryClient } from '@tanstack/react-query'
import { type HTMLAttributes, type ReactElement, useState } from 'react'
import toast from 'react-hot-toast'
import {
	Lock03Icon,
	RefreshCcw01Icon,
	RefreshCcw02Icon
} from '@/components/ui/icons'
import { DialogHint } from '@/features/marquee/components/dialog-hint'
import {
	ResponsiveConfirmDialog,
	ResponsiveConfirmTrigger
} from '@/features/marquee/components/responsive-confirm-dialog'
import { makeQueryKeys } from '@/features/marquee/query-keys'
import { invalidStateMessage } from '@/features/marquee/utils'
import { errorHandler } from '@/helpers/utils'
import { type MarqueeEventDetail, resolveEvent } from '@/sdk'
import { useTranslation } from '@/translations/i18n'

interface ResolveEventDialogProps {
	event: MarqueeEventDetail
	/**
	 * Has a CSV already gone out for this event? A re-run then rewrites accounts
	 * the CSV carried, and nothing in the response marks the divergence, so the
	 * confirmation says it before the press. Read from the export preview, which
	 * only exists once the event has closed.
	 */
	hasExported: boolean
	/** Element that opens the confirmation (the "Refresh accounts" button). */
	trigger: ReactElement<HTMLAttributes<HTMLElement>>
}

/**
 * "Refresh accounts from the lookup table?" confirmation. Runs
 * `POST /events/{id}/resolve` on confirm: the backend re-reads the global host
 * to account lookup table against every row whose host ID came from the import
 * and rewrites the cached accounts, so a corrected table takes effect on an
 * event that is already under way instead of waiting for it to close.
 *
 * Always confirmed, because the press is not additive: a row whose account was
 * corrected by hand but whose host ID is import-sourced gets whatever the table
 * says. Only `manual` host IDs are safe from it. The body draws the two cases
 * as a pair of hints, then the caveats as notes: the one about a deleted
 * mapping always, the Salesforce divergence once an export has gone out.
 *
 * The response is the whole refreshed roster (the `GET /attendees` shape plus
 * the two counters for this run), so it is written straight into the roster
 * cache rather than refetched: every tab reading the roster re-renders from it.
 * What does get invalidated is what derives from the roster elsewhere: the
 * event detail's tallies (exact key, so the roster underneath is not fetched a
 * second time), the tracker's gap count, and the export preview when the event
 * is closed and the preview is on screen.
 */
function ResolveEventDialog({
	event,
	hasExported,
	trigger
}: ResolveEventDialogProps) {
	const { t } = useTranslation()
	const [open, setOpen] = useState(false)
	const queryClient = useQueryClient()

	const resolve = useMutation({
		mutationFn: async () => {
			const response = await resolveEvent({
				path: { eventId: event.id }
			})
			return response.data
		},
		onSuccess: async (result) => {
			queryClient.setQueryData(
				makeQueryKeys.marquee.events.attendees(event.id),
				result
			)
			await Promise.all([
				queryClient.invalidateQueries({
					queryKey: makeQueryKeys.marquee.events.detail(event.id),
					exact: true
				}),
				queryClient.invalidateQueries({
					queryKey: makeQueryKeys.marquee.events.index()
				}),
				event.state === 'closed'
					? queryClient.invalidateQueries({
							queryKey:
								makeQueryKeys.marquee.events.exportPreview(
									event.id
								)
						})
					: undefined
			])
			setOpen(false)
			// The delta is the only thing that tells a run that fixed rows
			// from one that changed nothing; the absolute figures are already on
			// screen. Never the export: a resolved row still needs a present
			// check-in to reach it, so the CSV does not grow by this number.
			if (result.resolved_this_run > 0) {
				toast.success(
					t('marquee:resolve_event_toast_resolved', {
						count: result.resolved_this_run,
						considered: result.considered_this_run
					})
				)
			} else {
				toast(
					t('marquee:resolve_event_toast_none', {
						considered: result.considered_this_run
					})
				)
			}
		},
		onError: (error) => {
			// The 409 is the shared `invalid-state`, worded here: the button is
			// already withheld in `locked`, so this is the race where the event
			// moved under the operator from another tab.
			const refusal = invalidStateMessage({
				error,
				action: 'resolve_event',
				eventState: event.state
			})
			if (refusal) {
				toast.error(
					t(refusal.key, {
						state: refusal.stateKey ? t(refusal.stateKey) : ''
					})
				)
				return
			}
			errorHandler(error)
		}
	})

	const notes = [
		t('marquee:resolve_event_dialog_note_mapping'),
		...(hasExported
			? [t('marquee:resolve_event_dialog_note_exported')]
			: [])
	]

	return (
		<ResponsiveConfirmDialog
			body={
				<>
					<DialogHint
						description={t(
							'marquee:resolve_event_dialog_import_body'
						)}
						icon={<RefreshCcw02Icon />}
						title={t('marquee:resolve_event_dialog_import_title')}
					/>
					<DialogHint
						description={t(
							'marquee:resolve_event_dialog_manual_body'
						)}
						icon={<Lock03Icon />}
						title={t('marquee:resolve_event_dialog_manual_title')}
					/>
					<ul className='flex list-disc flex-col gap-1 pl-5 text-neutral-500 text-sm'>
						{notes.map((note) => (
							<li key={note}>{note}</li>
						))}
					</ul>
				</>
			}
			confirmLabel={
				<>
					<RefreshCcw01Icon />
					{t('marquee:resolve_event_dialog_confirm')}
				</>
			}
			description={t('marquee:resolve_event_dialog_description')}
			isPending={resolve.isPending}
			onConfirm={() => resolve.mutate()}
			onOpenChange={setOpen}
			open={open}
			title={t('marquee:resolve_event_dialog_title')}
		>
			<ResponsiveConfirmTrigger>{trigger}</ResponsiveConfirmTrigger>
		</ResponsiveConfirmDialog>
	)
}

export { ResolveEventDialog }
